import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;

// Define event names as constants to prevent typos
export const ANALYTICS_EVENTS = {
  // Authentication Events
  USER_SIGNED_IN: 'User Signed In',
  USER_SIGNED_UP: 'User Signed Up', 
  USER_SIGNED_OUT: 'User Signed Out',
  FORGOT_PASSWORD_REQUESTED: 'Forgot Password Requested',
  PASSWORD_RESET_COMPLETED: 'Password Reset Completed',
  
  // Navigation Events
  PAGE_VIEW: 'Page View',
  TAB_SWITCHED: 'Tab Switched',
  
  // Content Discovery Events
  CONTENT_SEARCHED: 'Content Searched',
  CONTENT_VIEWED: 'Content Viewed',
  SEARCH_FILTER_USED: 'Search Filter Used',
  
  // Favorites Events
  CONTENT_FAVORITED: 'Content Favorited',
  CONTENT_UNFAVORITED: 'Content Unfavorited',
  FAVORITES_VIEWED: 'Favorites Viewed',
  
  // Sharing Events
  CONTENT_SHARED: 'Content Shared',
  SHARED_CONTENT_VIEWED: 'Shared Content Viewed',
  
  // Meetup Events
  MEETUP_CREATED: 'Meetup Created',
  MEETUP_VIEWED: 'Meetup Viewed',
  MEETUP_JOINED: 'Meetup Joined',
  MEETUP_LEFT: 'Meetup Left',
  MEETUP_UPDATED: 'Meetup Updated',
  MEETUP_DELETED: 'Meetup Delete',
  MEETUP_SEARCHED: 'Meetup Searched',
  MEETUP_FILTERED: 'Meetup Filtered',
  BROWSE_MEETUPS_VIEWED: 'Browse Meetups Viewed',
  MY_MEETUPS_VIEWED: 'My Meetups Viewed',
  CREATE_MEETUP_STARTED: 'Create Meetup Started',
  
  // Profile Events
  PROFILE_VIEWED: 'Profile Viewed',
  PROFILE_UPDATED: 'Profile Updated',
  STREAMING_SERVICE_CONNECTED: 'Streaming Service Connected',
  STREAMING_SERVICE_DISCONNECTED: 'Streaming Service Disconnected',
  
  // Error Events
  ERROR_OCCURRED: 'Error Occurred',
  FORM_ERROR: 'Form Error',
  API_ERROR: 'API Error'
};

// Initialize Mixpanel
if (MIXPANEL_TOKEN) {
  mixpanel.init(MIXPANEL_TOKEN, {
    debug: process.env.NODE_ENV === 'development',
    track_pageview: false, // We'll handle this manually
    persistence: 'localStorage',
    ignore_dnt: true,
    secure_cookie: window.location.protocol === 'https:',
    loaded: function(mixpanel) {
      console.log('📊 Mixpanel Analytics loaded successfully for Box Seat');
    }
  });
} else {
  console.warn('⚠️ Mixpanel token not found, analytics disabled');
}

// Analytics utility functions
export const MixpanelAnalytics = {
  // Track events
  track: (eventName, properties = {}) => {
    try {
      if (MIXPANEL_TOKEN) {
        const eventProperties = {
          ...properties,
          app_name: 'Box Seat',
          timestamp: Date.now(),
          path: typeof window !== 'undefined' ? window.location.pathname : undefined,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
          screen_resolution: typeof screen !== 'undefined' ? `${screen.width}x${screen.height}` : undefined,
          environment: process.env.NODE_ENV
        };
        
        mixpanel.track(eventName, eventProperties);
        console.log(`📈 Analytics: ${eventName}`, eventProperties);
      }
    } catch (error) {
      console.error('❌ Error tracking event:', error);
    }
  },

  // Identify users
  identify: (userId, properties = {}) => {
    try {
      if (MIXPANEL_TOKEN) {
        mixpanel.identify(userId);
        mixpanel.people.set({
          $first_name: properties.firstName,
          $last_name: properties.lastName,
          $email: properties.email,
          $created: properties.createdAt,
          app_name: 'Box Seat',
          last_login: new Date().toISOString(),
          ...properties
        });
        console.log(`👤 User identified: ${userId}`);
      }
    } catch (error) {
      console.error('❌ Error identifying user:', error);
    }
  },

  // Track page views
  pageView: (pageName, properties = {}) => {
    try {
      if (MIXPANEL_TOKEN) {
        MixpanelAnalytics.track(ANALYTICS_EVENTS.PAGE_VIEW, {
          page_name: pageName,
          page_path: window.location.pathname,
          page_search: window.location.search,
          referrer: document.referrer,
          ...properties
        });
      }
    } catch (error) {
      console.error('❌ Error tracking page view:', error);
    }
  },

  // Reset analytics (on logout)
  reset: () => {
    try {
      if (MIXPANEL_TOKEN) {
        mixpanel.reset();
        console.log('🔄 Analytics reset');
      }
    } catch (error) {
      console.error('❌ Error resetting analytics:', error);
    }
  },

  // Track errors
  trackError: (errorMessage, errorContext = {}) => {
    try {
      if (MIXPANEL_TOKEN) {
        MixpanelAnalytics.track(ANALYTICS_EVENTS.ERROR_OCCURRED, {
          error_message: errorMessage,
          error_stack: errorContext.stack,
          error_component: errorContext.component,
          error_action: errorContext.action,
          ...errorContext
        });
      }
    } catch (error) {
      console.error('❌ Error tracking error:', error);
    }
  },

  // Update user properties
  updateUserProperties: (properties) => {
    try {
      if (MIXPANEL_TOKEN) {
        mixpanel.people.set(properties);
        console.log('👤 User properties updated:', properties);
      }
    } catch (error) {
      console.error('❌ Error updating user properties:', error);
    }
  }
};

// Export Mixpanel instance for advanced usage
export { mixpanel };
export default MixpanelAnalytics;